import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { KidsAiLogo } from "@/components/brand/KidsAiLogo";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Public-facing sticky header used on marketing/auth pages
 * (Pricing, Register, Install, etc.). Responsive: full nav on
 * md+, hamburger sheet on small screens.
 */
export const PublicHeader = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const links = [
    { to: "/pricing", label: "Pricing" },
    { to: "/install", label: "Install App" },
    { to: "/certificate/verify", label: "Verify Certificate" },
  ];

  return (
    <header className="sticky top-0 z-50 border-b-4 border-white bg-gradient-to-r from-kid-blue/20 via-kid-purple/20 to-kid-pink/20 backdrop-blur-lg shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 sm:h-20 items-center justify-between gap-3">
          <Link to="/" className="flex items-center min-w-0 flex-shrink">
            <KidsAiLogo className="h-9 sm:h-12 md:h-14 w-auto max-w-full" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to}>
                <Button variant="ghost" className="font-fredoka">
                  {l.label}
                </Button>
              </Link>
            ))}
            {user ? (
              <Button onClick={() => navigate("/dashboard")} className="font-fredoka">
                Dashboard
              </Button>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="font-fredoka">
                    Login
                  </Button>
                </Link>
                <Link to="/register/individual">
                  <Button className="font-fredoka bg-gradient-to-r from-primary to-secondary">
                    Sign Up Free
                  </Button>
                </Link>
              </>
            )}
          </nav>

          {/* Mobile sheet trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden h-11 w-11 rounded-full border-2 border-white/50"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <nav className="flex flex-col gap-2 mt-8">
                {links.map((l) => (
                  <Link key={l.to} to={l.to}>
                    <Button variant="ghost" className="w-full justify-start font-fredoka text-base">
                      {l.label}
                    </Button>
                  </Link>
                ))}
                <div className="h-px bg-border my-2" />
                {user ? (
                  <Button
                    className="w-full font-fredoka"
                    onClick={() => navigate("/dashboard")}
                  >
                    Go to Dashboard
                  </Button>
                ) : (
                  <>
                    <Link to="/login">
                      <Button variant="outline" className="w-full font-fredoka">
                        Login
                      </Button>
                    </Link>
                    <Link to="/register/individual">
                      <Button className="w-full font-fredoka bg-gradient-to-r from-primary to-secondary">
                        Sign Up Free
                      </Button>
                    </Link>
                  </>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default PublicHeader;
